<script setup lang="ts">
import { computed, onUnmounted, watch } from 'vue'
import { useNotificationStore } from '@/stores/notification.store'
import type { Notification, NotificationType } from '@/stores/notification.store'

// Stacked toast cards in a fixed top-right container. The component owns the
// auto-dismiss timers (only for rendered toasts) so hovering the stack pauses
// every countdown; the visual progress bar pauses in sync via CSS. Timing is
// JS-driven (not animationend) so reduced-motion users, whose animations are
// globally collapsed to 0ms, still get full read time.
const store = useNotificationStore()

const ICONS: Record<NotificationType, string> = {
	success: 'fa-solid fa-circle-check',
	error: 'fa-solid fa-circle-xmark',
	warning: 'fa-solid fa-triangle-exclamation',
	info: 'fa-solid fa-circle-info',
}

// Newest toast renders on top
const visible = computed(() => [...store.notifications].reverse())

interface TimerState {
	revision: number
	remaining: number
	startedAt: number
	handle: ReturnType<typeof setTimeout> | null
}

const timers = new Map<string, TimerState>()
let hovering = false

function schedule(id: string, state: TimerState) {
	state.startedAt = Date.now()
	state.handle = setTimeout(() => {
		timers.delete(id)
		store.remove(id)
	}, state.remaining)
}

function syncTimers(toasts: readonly Notification[]) {
	const alive = new Set(toasts.map((n) => n.id))
	for (const [id, state] of timers) {
		if (!alive.has(id)) {
			if (state.handle) clearTimeout(state.handle)
			timers.delete(id)
		}
	}
	for (const n of toasts) {
		if (n.timeout <= 0) continue
		const state = timers.get(n.id)
		if (state && state.revision === n.revision) continue
		// New toast, or a deduped repeat: (re)start the full countdown
		if (state?.handle) clearTimeout(state.handle)
		const fresh: TimerState = {
			revision: n.revision,
			remaining: n.timeout,
			startedAt: 0,
			handle: null,
		}
		timers.set(n.id, fresh)
		if (!hovering) schedule(n.id, fresh)
	}
}

watch(
	() => store.notifications.map((n) => `${n.id}:${n.revision}`).join('|'),
	() => syncTimers(store.notifications),
	{ immediate: true },
)

function pauseAll() {
	hovering = true
	for (const state of timers.values()) {
		if (!state.handle) continue
		clearTimeout(state.handle)
		state.handle = null
		state.remaining = Math.max(0, state.remaining - (Date.now() - state.startedAt))
	}
}

function resumeAll() {
	hovering = false
	for (const [id, state] of timers) {
		if (!state.handle) schedule(id, state)
	}
}

function handleAction(notification: Notification) {
	notification.onAction?.()
	store.remove(notification.id)
}

onUnmounted(() => {
	for (const state of timers.values()) {
		if (state.handle) clearTimeout(state.handle)
	}
	timers.clear()
})
</script>

<template>
	<Teleport to="body">
		<div
			class="notification-toast"
			aria-label="Notifications"
		>
			<TransitionGroup
				name="slide-right"
				tag="div"
				class="notification-toast__stack"
				@mouseenter="pauseAll"
				@mouseleave="resumeAll"
			>
				<div
					v-for="n in visible"
					:key="n.id"
					class="toast"
					:class="`toast--${n.type}`"
					:role="n.type === 'error' || n.type === 'warning' ? 'alert' : 'status'"
				>
					<div class="toast__icon">
						<i :class="ICONS[n.type]" />
					</div>

					<div class="toast__content">
						<p
							v-if="n.title"
							class="toast__title"
						>
							{{ n.title }}
						</p>
						<p class="toast__message">
							{{ n.message }}
							<span
								v-if="n.count > 1"
								class="toast__count"
								>×{{ n.count }}</span
							>
						</p>
					</div>

					<button
						v-if="n.actionLabel"
						type="button"
						class="toast__action"
						@click="handleAction(n)"
					>
						{{ n.actionLabel }}
					</button>

					<button
						type="button"
						class="toast__close"
						aria-label="Dismiss notification"
						@click="store.remove(n.id)"
					>
						<i class="fa-solid fa-xmark" />
					</button>

					<div
						v-if="n.timeout > 0"
						:key="`progress-${n.revision}`"
						class="toast__progress"
						:style="{ '--toast-duration': `${n.timeout}ms` }"
					/>
				</div>
			</TransitionGroup>
		</div>
	</Teleport>
</template>

<style scoped lang="scss">
// z-index 3100: toasts stay visible above the GlobalLoader (2900) and the
// route progress bar (3000)
.notification-toast {
	position: fixed;
	top: 16px;
	right: 16px;
	z-index: 3100;
	pointer-events: none;

	&__stack {
		display: flex;
		flex-direction: column;
		gap: 10px;
		align-items: flex-end;
	}
}

.toast {
	--toast-accent: var(--v-theme-info);

	position: relative;
	display: flex;
	align-items: flex-start;
	gap: 12px;
	width: 380px;
	max-width: calc(100vw - 32px);
	padding: 14px 44px 14px 16px;
	border-radius: 16px;
	overflow: hidden;
	background: rgb(var(--v-theme-surface));
	color: rgb(var(--v-theme-on-surface));
	border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
	border-left: 3px solid rgb(var(--toast-accent));
	box-shadow: $shadow-overlay;
	pointer-events: auto;

	&--success {
		--toast-accent: var(--v-theme-success);
	}
	&--error {
		--toast-accent: var(--v-theme-error);
	}
	&--warning {
		--toast-accent: var(--v-theme-warning);
	}

	&__icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 32px;
		height: 32px;
		flex-shrink: 0;
		border-radius: 10px;
		background: rgba(var(--toast-accent), 0.12);
		color: rgb(var(--toast-accent));
		font-size: 15px;

		i {
			animation: pop-in 300ms $ease-out-expo;
		}
	}

	&__content {
		flex: 1;
		min-width: 0;
		padding-top: 2px;
	}

	&__title {
		margin: 0 0 2px;
		font-size: 0.875rem;
		font-weight: 600;
		line-height: 1.3;
	}

	&__message {
		margin: 0;
		font-size: 0.8438rem;
		line-height: 1.45;
		color: rgba(var(--v-theme-on-surface), 0.8);
		overflow-wrap: break-word;
	}

	&__count {
		display: inline-block;
		margin-left: 6px;
		padding: 0 6px;
		border-radius: 999px;
		background: rgba(var(--toast-accent), 0.12);
		color: rgb(var(--toast-accent));
		font-size: 0.6875rem;
		font-weight: 700;
		line-height: 1.6;
		animation: pop-in 300ms $ease-out-expo;
	}

	&__action {
		align-self: center;
		flex-shrink: 0;
		padding: 4px 8px;
		border: none;
		border-radius: 8px;
		background: transparent;
		color: rgb(var(--toast-accent));
		font-size: 0.8125rem;
		font-weight: 600;
		cursor: pointer;
		transition: background-color $duration-fast $ease-standard;

		&:hover {
			background: rgba(var(--toast-accent), 0.08);
		}
	}

	&__close {
		position: absolute;
		top: 10px;
		right: 10px;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 24px;
		height: 24px;
		border: none;
		border-radius: 6px;
		background: transparent;
		color: rgba(var(--v-theme-on-surface), 0.45);
		font-size: 12px;
		cursor: pointer;
		transition:
			background-color $duration-fast $ease-standard,
			color $duration-fast $ease-standard;

		&:hover {
			background: rgba(var(--v-theme-on-surface), 0.06);
			color: rgba(var(--v-theme-on-surface), 0.8);
		}

		&:focus-visible {
			outline: 2px solid $focus-ring-color;
			outline-offset: 1px;
		}
	}

	// Remaining-time indicator; purely visual (JS timers own dismissal).
	// Pauses on stack hover together with the timers.
	&__progress {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		height: 3px;
		background: rgba(var(--toast-accent), 0.35);
		transform-origin: left;
		animation: toast-progress var(--toast-duration, 4000ms) linear forwards;
	}
}

.notification-toast__stack:hover .toast__progress {
	animation-play-state: paused;
}

// Take leaving toasts out of the flow so the rest of the stack reflows
// smoothly via .slide-right-move
.slide-right-leave-active {
	position: absolute;
	right: 0;
}
</style>
